import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import AgentAvatar from '../components/AgentAvatar';
import PhaseBadge, { PhaseKey, phaseConfig, PHASE_DISPLAY_NUMBER } from '../components/PhaseBadge';
import DeliveryModeExplainer from '../components/DeliveryModeExplainer';
import DeliveryModeSettingsCard from '../components/DeliveryModeSettingsCard';
import QaLanguageSettingsCard from '../components/QaLanguageSettingsCard';
import AutonomousTransitionsPanel from '../components/AutonomousTransitionsPanel';
import ProjectMembersPanel from '../components/ProjectMembersPanel';
import StakeholdersTab from '../components/settings/StakeholdersTab';
import MeetingsTab from '../components/meetings/MeetingsTab';
import InfrastructureCard from '../components/InfrastructureCard';
import ProjectDomainPackCard from '../components/ProjectDomainPackCard';
import DomainPackFailedNotice, { useDomainPackFailedNotice } from '../components/DomainPackFailedNotice';
import CodebaseSidebarCard from '../components/CodebaseSidebarCard';
import EscalationOverridesSidebarCard from '../components/EscalationOverridesSidebarCard';
import ProjectBrandingOverrideSidebarCard from '../components/ProjectBrandingOverrideSidebarCard';
import ProjectClaudeDesignOverrideSidebarCard from '../components/ProjectClaudeDesignOverrideSidebarCard';
import ClaudeDesignBundleImportCard from '../components/ClaudeDesignBundleImportCard';
import ClaudeDesignReadyBanner from '../components/ClaudeDesignReadyBanner';
import QAPolicyStatusCard from '../components/QAPolicyStatusCard';
import GateStatusBanner from '../components/GateStatusBanner';
import GitHubSetupBanner, { type GitHubBinding } from '../components/GitHubSetupBanner';
import GateReportPanel from '../components/GateReportPanel';
import Spinner from '../components/Spinner';
import PhaseConfigDialogue from '../components/PhaseConfigDialogue';
import StartSessionButton from '../components/StartSessionButton';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { PHASE_AGENTS } from '../config/phaseAgents';
import DeploymentDashboard from '../components/deployment/DeploymentDashboard';
import PhaseTransitionModal from '../components/PhaseTransitionModal';
import { HelpPopover } from '../components/help';
import ContinueSessionCTA from '../components/ContinueSessionCTA';
import CreateChangeOrderModal from '../components/CreateChangeOrderModal';
import CalibrationCard from '../components/CalibrationCard';
import SprintConfigCard from '../components/sprint/SprintConfigCard';
import Phase4SprintActions from '../components/sprint/Phase4SprintActions';
import { isProjectPastUxDesign, hasUxArtefacts as hasUxArtefactsHelper } from '../utils/standaloneUx';
// Sprint A.9 R3 audit fix — single source of truth for the 6-value status
// union. The local copy of these types at this file's top used a 4-value
// union (pending/active/completed/blocked) which dropped `awaiting_signoff`
// and `changes_requested` on the floor. The Phase Timeline card silently
// degraded those two statuses to grey/no-tooltip, contradicting the new
// stepper's amber/red treatment on the same page.
import type { PhaseTimelineItem } from '../hooks/useProjectPhases';
import { useMyTenant } from '../hooks/useMyTenant';
import { VISIBLE_PHASES } from '../components/ProjectPhaseStepper';

interface PhaseState {
  id: string;
  phase: PhaseKey;
  status: string;
  enteredAt: string | null;
  exitedAt: string | null;
}

interface AgentSession {
  id: string;
  agentName: string;
  agentDisplayName?: string;
  status: string;
  summary: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Artefact {
  id: string;
  type: string;
  title: string;
  currentVersion: string;
  createdAt: string;
  updatedAt: string;
}

interface ActivityItem {
  id: string;
  text: string;
  time: string;
  icon: string;
}

interface ProtectionStatus {
  tier: string;
  canProtect: boolean;
  protected: boolean;
  message: string;
}

interface ActiveSession {
  id: string;
  agentName: string;
  agentDisplayName?: string;
  status: string;
  lastMessageAt: string;
}

interface ProjectDetail {
  id: string;
  /** The project's own workspace (the projects row); the Stakeholders tab compares it with the selected one. */
  tenantId?: string;
  name: string;
  productArea: string | null;
  currentPhase: PhaseKey;
  createdAt: string;
  updatedAt: string;
  currentPhaseState: PhaseState | null;
  latestSession: AgentSession | null;
  activeSession: ActiveSession | null;
  artefactCount: number;
  latestArtefact: Artefact | null;
  recentActivity: ActivityItem[];
  // Sprint 12 DOMAIN.A2 — null when the project hasn't selected a
  // pack (defaults to Generic at runtime).
  domainPackId?:   string | null;
  domainPackType?: 'generic' | 'healthcare' | 'bfsi' | null;
  // Sprint 12.5 ACC.B1 (#415) — Track B delivery mode flag, surfaced
  // by GET /projects/:id (B1 already includes it via the row spread).
  // ACC.B3 (#417) renders the DeliveryModeBadge from this value.
  deliveryMode?: 'governed' | 'accelerate';
  // AUTO.2 PR2 (#1404 follow-on) — per-project QA test-execution language,
  // surfaced by GET /projects/:id via the row spread. Drives the Phase-6
  // auto-trigger's runPrReviewCoordinator invocation; see projects.ts
  // schema comment for why these exact literal values matter.
  language?: 'typescript' | 'python' | 'go';
  // Sprint UXV UXV.F4 (#1420) — per-project Claude Design export override,
  // surfaced by GET /projects/:id via the row spread. null/undefined = inherit
  // the tenant default; true/false = override.
  claudeDesignExportEnabled?: boolean | null;
  // #1115B S6 — denormalised GitHub binding state from infrastructure_state.
  // null when no GITHUB_REPO row exists for this project. The
  // GitHubSetupBanner shows whenever this is null or state !== 'PROVISIONED'.
  githubBinding?: GitHubBinding | null;
}

interface ProjectSession {
  id: string;
  phase: string;
  agentName: string;
  agentDisplayName?: string;
  status: string;
  summary: string | null;
  createdAt: string;
  updatedAt: string;
  selectedIssueTitle?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface AllPhaseState {
  id: string;
  phase: string;
  status: string;
  enteredAt: string | null;
  exitedAt: string | null;
}

// PhaseTimelineItem now imported from ../hooks/useProjectPhases (single
// source of truth for the 6-value status union — R3 fix).

interface PhaseStatesResponse {
  currentPhase: string | null;
  phases: PhaseTimelineItem[];
}

const phaseDescriptions: Record<PhaseKey, string> = {
  phase_0_bootstrap:       'Project setup and initial configuration',
  phase_1_requirements:    'Defining product requirements and user stories',
  phase_2_architecture:    'System design and technical architecture',
  phase_2_5_ux_design:     'User journeys, personas, and UX specifications',
  phase_3_sprint_planning: 'Sprint planning and backlog refinement',
  phase_4_development:     'Active development and implementation',
  phase_5_code_review:     'Code review and quality assurance',
  phase_6_testing:         'Testing, QA, and bug fixes',
  phase_7_deployment:      'Release and deployment to production',
  phase_8_retrospective:   'Sprint retrospective and process improvement',
};

const ALL_PHASES: PhaseKey[] = [
  'phase_0_bootstrap',
  'phase_1_requirements',
  'phase_2_architecture',
  'phase_2_5_ux_design',
  'phase_3_sprint_planning',
  'phase_4_development',
  'phase_5_code_review',
  'phase_6_testing',
  'phase_7_deployment',
  'phase_8_retrospective',
];

// SL.L2 (#2958, ruling 6253570495 item 2): the product backlog exists from
// Phase 3 entry (SL.B1's phase-entry step), so the project-level link shows
// from Phase 3 through Phase 8.
const BACKLOG_PHASES: ReadonlySet<PhaseKey> = new Set<PhaseKey>([
  'phase_3_sprint_planning', 'phase_4_development', 'phase_5_code_review',
  'phase_6_testing', 'phase_7_deployment', 'phase_8_retrospective',
]);

const ARTEFACT_ICONS: Record<string, string> = {
  prd: '📄',
  sad: '🏗️',
  erd: '🗂️',
  sprint_plan: '📅',
  test_plan: '🧪',
  deployment_runbook: '🚀',
  retro_report: '🔍',
  user_stories: '📝',
  analysis_report: '🔬',
  user_journey_map: '🗺️',
  screen_inventory: '🖥️',
  screen_spec: '🧩',
  component_catalogue: '🧱',
  design_tokens: '🎨',
};

const ARTEFACT_TYPE_COLORS: Record<string, string> = {
  prd: '#3B82F6',
  sad: '#7C3AED',
  adr: '#EC4899',
  user_stories: '#10B981',
  analysis_report: '#F59E0B',
  user_journey_map: '#DB2777',
  screen_inventory: '#DB2777',
  screen_spec: '#DB2777',
  component_catalogue: '#DB2777',
  design_tokens: '#DB2777',
  sprint_plan: '#3B82F6',
  erd: '#6366F1',
  test_plan: '#8B5CF6',
  deployment_runbook: '#14B8A6',
  retro_report: '#F97316',
  handoff_package: '#78716C',
  gate_report: '#F59E0B',
};

// PHASE_AGENTS moved to ../config/phaseAgents so its "every phase has a
// dashboard entry" invariant can be unit-tested without rendering the
// 2000-line dashboard component.

const PHASE_ARTEFACT_TYPES: Partial<Record<PhaseKey, string[]>> = {
  phase_1_requirements: ['prd', 'user_stories', 'analysis_report'],
  phase_2_architecture: ['sad', 'adr'],
  phase_2_5_ux_design: ['user_journey_map', 'screen_inventory', 'screen_spec', 'component_catalogue', 'design_tokens'],
  phase_3_sprint_planning: ['sprint_plan', 'sprint_backlog'],
  phase_4_development: ['handoff_package'],
};

const ARTEFACT_PHASE_MAP: Record<string, { phase: string; shortName: string | null; icon: string }> = {
  prd:               { phase: 'Phase 1: Requirements',    shortName: 'PRD',             icon: '📄' },
  user_stories:      { phase: 'Phase 1: Requirements',    shortName: 'User Stories',    icon: '📋' },
  analysis_report:   { phase: 'Phase 1: Requirements',    shortName: 'Analysis Report', icon: '🔍' },
  sad:               { phase: 'Phase 2: Architecture',    shortName: 'SAD',             icon: '📐' },
  adr:               { phase: 'Phase 2: Architecture',    shortName: 'ADRs',            icon: '📝' },
  user_journey_map:    { phase: 'Phase 2.5: UX Design',     shortName: 'Journey Map',     icon: '🗺️' },
  screen_inventory:    { phase: 'Phase 2.5: UX Design',     shortName: 'Screen Inventory', icon: '🖥️' },
  screen_spec:         { phase: 'Phase 2.5: UX Design',     shortName: 'Screen Specs',    icon: '🧩' },
  component_catalogue: { phase: 'Phase 2.5: UX Design',     shortName: 'Component Catalogue', icon: '🧱' },
  design_tokens:       { phase: 'Phase 2.5: UX Design',     shortName: 'Design Tokens',   icon: '🎨' },
  sprint_plan:         { phase: 'Phase 3: Sprint Planning', shortName: 'Sprint Plan',     icon: '📊' },
  sprint_backlog:    { phase: 'Phase 3: Sprint Planning', shortName: 'Sprint Backlog',  icon: '📦' },
  handoff_package:   { phase: 'Handoff Packages',         shortName: null,              icon: '📨' },
  gate_report:       { phase: 'Phase Gates',              shortName: null,              icon: '🚦' },
  amendment_request: { phase: 'Other',                    shortName: null,              icon: '📝' },
};

const ARTEFACT_PHASE_ORDER = [
  'Phase 1: Requirements',
  'Phase 2: Architecture',
  'Phase 2.5: UX Design',
  'Phase 3: Sprint Planning',
  'Handoff Packages',
  'Phase Gates',
  'Other',
];

const PHASE_TO_ARTEFACT_PHASE: Partial<Record<PhaseKey, string>> = {
  phase_1_requirements:    'Phase 1: Requirements',
  phase_2_architecture:    'Phase 2: Architecture',
  phase_2_5_ux_design:     'Phase 2.5: UX Design',
  phase_3_sprint_planning: 'Phase 3: Sprint Planning',
  phase_4_development:     'Handoff Packages',
};

function statusPillStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'draft':
      return { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' };
    case 'active':
    case 'approved':
      return { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' };
    case 'handed_off':
      return { background: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' };
    case 'superseded':
      return { background: 'rgba(239,68,68,0.12)', color: 'var(--error)' };
    default:
      return { background: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' };
  }
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ArtefactListItem {
  id: string;
  type: string;
  title: string;
  currentVersion: string;
  // Full artefact_status enum (apps/api/src/db/schema/artefacts.ts) — a
  // claude_design_bundle lands 'needs_review' on ingest, so the union MUST
  // carry it (and 'imported') or the DISC.B2 tracker reads a typed-away status.
  status: 'draft' | 'active' | 'superseded' | 'approved' | 'handed_off' | 'imported' | 'needs_review';
  // DISC.B2 (#1477) — surfaced by GET /artefacts (already on the wire); the
  // Claude Design bundle stage tracker shows it as the "imported" date.
  createdAt: string;
}

// Shared card container style
const cardCls = 'rounded-xl border shadow-sm hover:shadow-md transition-shadow duration-150 hover:border-stone-300';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderColor: 'var(--border)',
  padding: 'var(--space-5)',
};

// Card section header
function SectionHeader({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <p
      className="uppercase tracking-wider"
      style={{
        fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 'var(--space-3)', letterSpacing: '0.05em',
        ...(color ? { borderLeft: `3px solid ${color}`, paddingLeft: 8 } : {}),
      }}
    >
      {children}
    </p>
  );
}

export default function ProjectDashboardPage() {
  // Sprint A.9 — nested layout uses :projectId at the parent; alias on
  // destructure preserves the internal `id` variable name (~70 references).
  const { projectId: id } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // MODE.A2 (#1264) — tenant billing state gates the delivery-mode switch
  // card (TRIAL-only, fail-closed). Fires one GET /tenants/me/membership on
  // mount (empty dep array inside the hook); does not re-fetch per render.
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [artefacts, setArtefacts] = useState<ArtefactListItem[]>([]);
  const [projectLoading, setProjectLoading] = useState(true);
  const [artefactsLoading, setArtefactsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState<ProjectSession[]>([]);
  const [allPhaseStates, setAllPhaseStates] = useState<AllPhaseState[]>([]);
  const [expandedPhase, setExpandedPhase] = useState<PhaseKey | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [protectionStatus, setProtectionStatus] = useState<ProtectionStatus | null>(null);
  const [handingOff, setHandingOff] = useState(false);
  const [handoffError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [pendingAmendmentCount, setPendingAmendmentCount] = useState(0);
  const [pendingRefinementCount, setPendingRefinementCount] = useState(0);
  // #2370 GAP 1 — a resolved open question with no PENDING suggestion row
  // yet (never captured, or captured-but-not-yet-generated) makes
  // pendingRefinementCount stay 0 and the card below falsely read "No
  // pending suggestions" even though a resolved answer is sitting
  // uncaptured. This tracks that separately so the card can tell the two
  // states apart.
  const [unincorporatedResolvedCount, setUnincorporatedResolvedCount] = useState(0);
  const [staleArtefactCount, setStaleArtefactCount] = useState(0);
  const [showCreateCO, setShowCreateCO] = useState(false);
  const [decisionsCount, setDecisionsCount] = useState<{ total: number; accepted: number } | null>(null);
  const [riskSummary, setRiskSummary] = useState<{ total: number; open: number; high: number; critical: number } | null>(null);
  // #2612 — NFR-disposition affordance count (parity with Risks/Open
  // Questions above). Only countUndisposedNfrs's bare count exists for NFRs
  // (no full summary route like Risks' /risks/summary), so this card is
  // gated on count > 0 rather than always-shown like the Risks card — a bare
  // "0 undisposed" card carries no information the way Risks' open/high/
  // critical breakdown does. null = not yet loaded (card hidden).
  const [undisposedNfrsCount, setUndisposedNfrsCount] = useState<number | null>(null);
  const [evidenceSummary, setEvidenceSummary] = useState<{ totalLinks: number; requirements: number; userStories: number; adrs: number } | null>(null);
  const [expandedArtefactPhases, setExpandedArtefactPhases] = useState<Set<string>>(new Set());
  const [openIssueCount, setOpenIssueCount] = useState<number | null>(null);
  const [entryGateSummary, setEntryGateSummary] = useState<{ hasFailures: boolean; failureCount: number; gateType?: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'overview' | 'team' | 'stakeholders' | 'meetings'>(
    searchParams.get('tab') === 'team'         ? 'team'         :
    searchParams.get('tab') === 'stakeholders' ? 'stakeholders' :
    searchParams.get('tab') === 'meetings'     ? 'meetings'     :
    'overview',
  );

  // Sync activeTab when URL search params change (e.g. gate report "Assign Architect" link)
  useEffect(() => {
    const tab = searchParams.get('tab');
    setActiveTab(
      tab === 'team'         ? 'team'         :
      tab === 'stakeholders' ? 'stakeholders' :
      tab === 'meetings'     ? 'meetings'     :
      'overview',
    );
  }, [searchParams]);
  // #3505 — page-level, before the loading/error returns: the notice state
  // must survive a tab switch (the Overview content unmounts), and the URL is
  // stripped before the project has loaded. It is tied to this route's project
  // id, since the page is not remounted when only :projectId changes (R3 N2).
  const { notice: packNotice, dismiss: dismissPackNotice } = useDomainPackFailedNotice(id);
  const [phaseTimeline, setPhaseTimeline] = useState<PhaseTimelineItem[]>([]);

  // Sprint A.9 — consume the ?phase=<key> query param set by
  // ProjectPhaseStepper's click handler. Two responsibilities, two effects
  // (R1+R2 H1 fix — the original single-effect version raced with project
  // load; #phase-progress-card lives in the success-render block which
  // wasn't mounted yet when the effect first fired):
  //
  //   (1) setExpandedPhase fires immediately on phase param change. The
  //       accordion only renders the expanded UI for completed phases
  //       (existing isCompleted gate at line ~2074), but setting state
  //       eagerly is safe — render handles the rest. P3 follow-on #1166
  //       tracks extending the accordion to active phases.
  //
  //   (2) scrollIntoView waits for !projectLoading so the anchor exists.
  //       Re-fires when projectLoading transitions to false even if the
  //       phase param hasn't changed.
  //
  // L2/M2: validates against VISIBLE_PHASES (the 8 the stepper actually
  // emits), not ALL_PHASES (which would allow manually crafted URLs to
  // pin phase_0_bootstrap / phase_8_retrospective). M2: extracting `phase`
  // so the effect dep is the param value (not the full searchParams) means
  // tab-switches like ?tab=team no longer re-fire the phase effect.
  const phaseQueryParam = searchParams.get('phase');

  useEffect(() => {
    if (!phaseQueryParam) return;
    if ((VISIBLE_PHASES as readonly string[]).includes(phaseQueryParam)) {
      setExpandedPhase(phaseQueryParam as PhaseKey);
    }
  }, [phaseQueryParam]);

  useEffect(() => {
    if (!phaseQueryParam) return;
    if (projectLoading) return;
    // 50ms lets the success block paint after projectLoading clears.
    const t = setTimeout(() => {
      document.getElementById('phase-progress-card')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    return () => clearTimeout(t);
  }, [phaseQueryParam, projectLoading]);
  const [exitGateResult, setExitGateResult] = useState<{ overallResult: 'PASS' | 'FAIL' | 'PASS_WITH_OVERRIDE'; items: { title: string; status: string; actionRequired?: string }[] } | null>(null);
  const [exitGatePanelOpen, setExitGatePanelOpen] = useState(false);
  const [showConfigDialogue, setShowConfigDialogue] = useState(false);
  const [configComplete, setConfigComplete] = useState(false);
  const [transitionModal, setTransitionModal] = useState<{
    toPhaseNumber: number;
    direction: 'ENTRY' | 'EXIT';
    fromPhase: string;
  } | null>(null);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [creatingNewSession, setCreatingNewSession] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Sprint 11 UX.S1 — standalone UX flow state
  const [startingStandaloneUx, setStartingStandaloneUx] = useState(false);
  const [showStandaloneUxConfirm, setShowStandaloneUxConfirm] = useState(false);
  const [standaloneUxError, setStandaloneUxError] = useState('');
  const artefactPhasesInitRef = useRef<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const sessionHistoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingName) nameInputRef.current?.select();
  }, [editingName]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setProjectLoading(true);
    setArtefactsLoading(true);
    setError('');

    apiFetch<ProjectDetail>(`/projects/${id}`)
      .then(v => { if (!cancelled) setProject(v); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load project'); })
      .finally(() => { if (!cancelled) setProjectLoading(false); });

    apiFetch<ArtefactListItem[]>(`/artefacts?projectId=${id}`)
      .then(v => { if (!cancelled) setArtefacts(v); })
      .catch(() => {/* show empty state */})
      .finally(() => { if (!cancelled) setArtefactsLoading(false); });

    apiFetch<ProjectSession[]>(`/projects/${id}/sessions`).then(v => { if (!cancelled) setSessions(v); }).catch(() => {});
    apiFetch<AllPhaseState[]>(`/projects/${id}/phases`).then(v => { if (!cancelled) setAllPhaseStates(v); }).catch(() => {});
    apiFetch<PhaseStatesResponse>(`/projects/${id}/phase-states`).then(v => { if (!cancelled) setPhaseTimeline(v.phases); }).catch(() => {});
    setPendingAmendmentCount(0);
    setStaleArtefactCount(0);
    setPendingRefinementCount(0);
    setUnincorporatedResolvedCount(0);
    setUndisposedNfrsCount(null);
    apiFetch<{ id: string }[]>(`/projects/${id}/amendments?status=proposed`).then(r => { if (!cancelled) setPendingAmendmentCount(r.length); }).catch(() => {});
    apiFetch<{ id: string }[]>(`/projects/${id}/refinement-suggestions?status=pending`).then(r => { if (!cancelled) setPendingRefinementCount(r.length); }).catch(() => {});
    // #2370 GAP 1 — see unincorporatedResolvedCount's own declaration above.
    apiFetch<{ count: number }>(`/projects/${id}/open-questions/unincorporated-count`).then(r => { if (!cancelled) setUnincorporatedResolvedCount(r.count); }).catch(() => {});
    apiFetch<{ id: string }[]>(`/projects/${id}/staleness`).then(r => { if (!cancelled) setStaleArtefactCount(r.length); }).catch(() => {});
    apiFetch<{ status: string }[]>(`/projects/${id}/decisions`).then(r => { if (!cancelled) setDecisionsCount({ total: r.length, accepted: r.filter(d => d.status === 'ACCEPTED').length }); }).catch(() => {});
    apiFetch<{ total: number; open: number; high: number; critical: number }>(`/projects/${id}/risks/summary`).then(r => { if (!cancelled) setRiskSummary(r); }).catch(() => {});
    // #2612 — see undisposedNfrsCount's own declaration above.
    apiFetch<{ count: number }>(`/projects/${id}/nfrs/undisposed-count`).then(r => { if (!cancelled) setUndisposedNfrsCount(r.count); }).catch(() => {});
    apiFetch<{ totalLinks: number; requirements: number; userStories: number; adrs: number }>(`/projects/${id}/evidence/summary`).then(r => { if (!cancelled) setEvidenceSummary(r); }).catch(() => {});
    apiFetch<{ id: string }[]>(`/projects/${id}/issues?status=OPEN`).then(r => { if (!cancelled) setOpenIssueCount(r.length); }).catch(() => {});
    apiFetch<{ hasFailures: boolean; failureCount: number }>(`/projects/${id}/gate-summary`).then(r => { if (!cancelled) setEntryGateSummary(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Fetch exit gate for current phase (phases 1–3 have handoff buttons)
  const EXIT_GATE_PHASES: Record<string, number> = {
    phase_1_requirements: 1,
    phase_2_architecture: 2,
    phase_2_5_ux_design: 25,
    phase_3_sprint_planning: 3,
  };
  useEffect(() => {
    if (!project || !id) return;
    const phaseNum = EXIT_GATE_PHASES[project.currentPhase];
    if (!phaseNum) { setExitGateResult(null); return; }
    apiFetch<{ overallResult: 'PASS' | 'FAIL' | 'PASS_WITH_OVERRIDE'; items: { title: string; status: string; actionRequired?: string }[] }>(
      `/projects/${id}/phases/${phaseNum}/gate/exit`,
    )
      .then(setExitGateResult)
      .catch(() => setExitGateResult(null));
  }, [project?.currentPhase, id]);

  useEffect(() => {
    if (!project || artefactPhasesInitRef.current === project.id) return;
    artefactPhasesInitRef.current = project.id;

    // Find the latest phase (by SDLC order) that has artefacts — excluding Handoff Packages / Other
    const EXPANDABLE_PHASES = ['Phase 1: Requirements', 'Phase 2: Architecture', 'Phase 2.5: UX Design', 'Phase 3: Sprint Planning'];
    const phasesWithArtefacts = new Set(
      artefacts.map(a => ARTEFACT_PHASE_MAP[a.type]?.phase ?? 'Other').filter(p => EXPANDABLE_PHASES.includes(p))
    );

    // Walk from last to first to find the most advanced phase that has artefacts
    let mostRecentPhase = '';
    for (let i = EXPANDABLE_PHASES.length - 1; i >= 0; i--) {
      if (phasesWithArtefacts.has(EXPANDABLE_PHASES[i])) {
        mostRecentPhase = EXPANDABLE_PHASES[i];
        break;
      }
    }

    const expanded = new Set<string>();
    if (mostRecentPhase) {
      const idx = EXPANDABLE_PHASES.indexOf(mostRecentPhase);
      expanded.add(mostRecentPhase);
      if (idx > 0) expanded.add(EXPANDABLE_PHASES[idx - 1]);
    } else {
      expanded.add('Phase 1: Requirements');
    }
    setExpandedArtefactPhases(expanded);
  }, [project, artefacts]);

  const groupedArtefacts = useMemo(() => {
    const groups: Record<string, ArtefactListItem[]> = {};
    for (const artefact of artefacts) {
      const phase = ARTEFACT_PHASE_MAP[artefact.type]?.phase ?? 'Other';
      if (!groups[phase]) groups[phase] = [];
      groups[phase].push(artefact);
    }
    return ARTEFACT_PHASE_ORDER
      .filter(p => groups[p]?.length > 0)
      .map(phase => ({ phase, items: groups[phase] }));
  }, [artefacts]);

  const toggleArtefactPhase = (phase: string) => {
    setExpandedArtefactPhases(prev => {
      const next = new Set(prev);
      if (next.has(phase)) next.delete(phase);
      else next.add(phase);
      return next;
    });
  };

  const handleRunAnalysis = async () => {
    if (!id || artefacts.length === 0) return;
    setAnalysing(true);
    try {
      const data = await apiFetch<{ reportId: string }>(`/projects/${id}/analyse`, {
        method: 'POST',
        body: JSON.stringify({ artefactIds: artefacts.map(a => a.id) }),
      });
      navigate(`/artefacts/${data.reportId}`);
    } catch {
      setAnalysing(false);
    }
  };

  const handleHandoff = () => setTransitionModal({ toPhaseNumber: 2, direction: 'EXIT', fromPhase: 'phase_1_requirements' });
  // Sprint 11 UX.A1: Phase 2 (Architecture) now hands off to Phase 2.5 (UX Design)
  // before Sprint Planning. The transition modal expects a single number; 25 is
  // the URL-safe form mirrored on the backend (PHASE_NUMBER in handoff.ts).
  const handleHandoff2to25 = () => setTransitionModal({ toPhaseNumber: 25, direction: 'EXIT', fromPhase: 'phase_2_architecture' });
  const handleHandoff25to3 = () => setTransitionModal({ toPhaseNumber: 3, direction: 'EXIT', fromPhase: 'phase_2_5_ux_design' });
  const handleHandoff3to4 = () => setTransitionModal({ toPhaseNumber: 4, direction: 'EXIT', fromPhase: 'phase_3_sprint_planning' });

  const handleArchive = async () => {
    if (!id) return;
    setArchiving(true);
    try {
      await apiFetch(`/projects/${id}/archive`, { method: 'PATCH' });
      navigate('/projects');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive project');
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !project || confirmName !== project.name) return;
    setDeleting(true);
    try {
      await apiFetch(`/projects/${id}`, { method: 'DELETE' });
      navigate('/projects');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setConfirmName('');
    }
  };

  const devSessions = useMemo(
    () => sessions.filter(s => s.agentName === 'dev_agent'),
    [sessions],
  );

  // Sprint 11 UX.S1 — Standalone UX Agent flow. Creates a fresh ux_design
  // session in standalone mode (no phase transition, project phase stays where
  // it is) and navigates to the chat page. The button only renders when the
  // project is past Phase 2.5 and no UX artefacts exist; the "regenerate"
  // confirmation handles the duplicate case.
  const startStandaloneUxSession = async () => {
    if (!id) return;
    setStartingStandaloneUx(true);
    setStandaloneUxError('');
    try {
      const session = await apiFetch<{ id: string }>(`/projects/${id}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ agentType: 'ux_design', mode: 'standalone' }),
      });
      navigate(`/projects/${id}/chat?sessionId=${session.id}`);
    } catch (err) {
      setStandaloneUxError(err instanceof Error ? err.message : 'Failed to start UX session');
      setStartingStandaloneUx(false);
    }
  };

  const handleGenerateUxClick = () => {
    if (hasUxArtefactsHelper(artefacts)) {
      setShowStandaloneUxConfirm(true);
      return;
    }
    void startStandaloneUxSession();
  };

  const handleConfirmNewSession = async () => {
    if (!id) return;
    setCreatingNewSession(true);
    try {
      await apiFetch<{ id: string }>(`/projects/${id}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ agentType: 'dev_agent', forceNew: true }),
      });
      navigate(`/projects/${id}/chat?agentType=dev_agent`);
    } catch {
      setCreatingNewSession(false);
    }
    setShowNewSessionModal(false);
  };

  // Close session history dropdown on outside click
  useEffect(() => {
    if (!showSessionHistory) return;
    const handler = (e: MouseEvent) => {
      if (sessionHistoryRef.current && !sessionHistoryRef.current.contains(e.target as Node)) {
        setShowSessionHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSessionHistory]);

  const handleTransitionSuccess = async () => {
    setTransitionModal(null);
    if (!id) return;
    setHandingOff(true);
    try {
      await apiFetch(`/projects/${id}/handoff`, { method: 'POST' });
    } catch {
      // handoff may not be needed if transition alone suffices
    }
    try {
      const [updatedProject, updatedArtefacts] = await Promise.all([
        apiFetch<ProjectDetail>(`/projects/${id}`),
        apiFetch<ArtefactListItem[]>(`/artefacts?projectId=${id}`),
      ]);
      setProject(updatedProject);
      setArtefacts(updatedArtefacts);
    } catch {
      // reload as fallback
      window.location.reload();
    } finally {
      setHandingOff(false);
    }
  };

  // SL.L3a (#2959): after a sprint close on the Phase 4 card, re-read the open-issue count.
  const reloadOpenIssueCount = () => {
    apiFetch<{ id: string }[]>(`/projects/${id}/issues?status=OPEN`)
      .then(r => setOpenIssueCount(r.length))
      .catch(() => {});
  };

  const saveProjectName = async () => {
    const trimmed = nameValue.trim();
    setEditingName(false);
    if (!trimmed || !project || trimmed === project.name) return;
    try {
      const updated = await apiFetch<{ name: string }>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
      });
      setProject(prev => prev ? { ...prev, name: updated.name } : prev);
    } catch {
      // silently revert
    }
  };

  if (projectLoading) {
    return (
      <div className="flex-1 px-8 py-6">
        <div className="animate-pulse space-y-6">
          <div className="space-y-2">
            <div className="h-3 rounded w-24" style={{ background: 'var(--surface-alt)' }} />
            <div className="h-7 rounded w-64" style={{ background: 'var(--surface-alt)' }} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-xl border h-32" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} />
              <div className="rounded-xl border h-48" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} />
            </div>
            <div className="space-y-4">
              <div className="rounded-xl border h-32" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} />
              <div className="rounded-xl border h-64" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex-1 px-8 py-6">
        <p className="mb-2" style={{ color: 'var(--error)' }}>{error || 'Project not found'}</p>
        <Link to="/projects" className="text-sm hover:underline" style={{ color: 'var(--primary)' }}>
          ← Back to Projects
        </Link>
      </div>
    );
  }

  const currentPhaseIndex = ALL_PHASES.indexOf(project.currentPhase);
  const currentPhaseTimeline = phaseTimeline.find(p => p.phase === project.currentPhase);
  const currentPhaseConfigStatus = configComplete ? 'COMPLETE' : (currentPhaseTimeline?.configStatus ?? 'NOT_STARTED');
  const needsConfig = currentPhaseConfigStatus !== 'COMPLETE' && ['phase_1_requirements', 'phase_2_architecture', 'phase_2_5_ux_design', 'phase_3_sprint_planning', 'phase_4_development'].includes(project.currentPhase);
  const hasSessions = project.latestSession !== null;
  const hasActiveSession = project.activeSession !== null;
  const activeSessionRecent = hasActiveSession
    && (Date.now() - new Date(project.activeSession!.lastMessageAt).getTime() < 24 * 60 * 60 * 1000);
  const exitGateFailed = exitGateResult?.overallResult === 'FAIL';
  const showHandoffButton = project.currentPhase === 'phase_1_requirements';
  const canHandoff = showHandoffButton && !handingOff;
  const showHandoff2to25 = project.currentPhase === 'phase_2_architecture';
  const canHandoff2to25 = showHandoff2to25 && !handingOff;
  const showHandoff25to3 = project.currentPhase === 'phase_2_5_ux_design';
  const canHandoff25to3 = showHandoff25to3 && !handingOff;
  const showHandoff3to4 = project.currentPhase === 'phase_3_sprint_planning';
  const canHandoff3to4 = showHandoff3to4 && !handingOff;
  const showDevButton  = project.currentPhase === 'phase_4_development';
  // Sprint 11 UX.S1 — Standalone "Generate UX Specs" visibility rules:
  //   • Project is past Phase 2.5 (Phase 3 / 4 / 5+) — phases ≤ 2.5 use the
  //     gated flow ("Hand Off to UX Design" → Phase 2.5 chat).
  //   • When a journey map already exists, the button label switches to
  //     "Regenerate" and the click opens a confirmation modal.
  const projectPastUxDesign = isProjectPastUxDesign(project.currentPhase);
  const hasUxArtefacts = hasUxArtefactsHelper(artefacts);
  const showStandaloneUxButton = projectPastUxDesign;
  const hasOpenIssues  = (openIssueCount ?? 0) > 0;
  const lastActivityTime =
    project.recentActivity && project.recentActivity.length > 0
      ? project.recentActivity[0].time
      : project.updatedAt;

  return (
    <div className="flex-1 overflow-auto">
        {/* Top bar */}
        <div
          className="px-8 py-5 border-b sticky top-0 z-10"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-1.5 text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
            <Link to="/projects" className="hover:underline transition-colors" style={{ color: 'var(--text-secondary)' }}>
              ← Projects
            </Link>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {editingName ? (
                <div className="flex items-center gap-1.5">
                  <input
                    ref={nameInputRef}
                    value={nameValue}
                    onChange={e => setNameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); void saveProjectName(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingName(false); }
                    }}
                    onBlur={() => setEditingName(false)}
                    maxLength={100}
                    className="text-2xl font-bold outline-none bg-transparent leading-tight border-b-2"
                    style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', borderColor: 'var(--primary)' }}
                  />
                  <button
                    onMouseDown={e => { e.preventDefault(); void saveProjectName(); }}
                    className="font-bold text-lg leading-none"
                    style={{ color: 'var(--primary)' }}
                    aria-label="Save project name"
                  >
                    ✓
                  </button>
                </div>
              ) : (
                <div className="group flex items-center gap-2">
                  <h1
                    style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}
                  >
                    {project.name}
                  </h1>
                  <button
                    onClick={() => { setNameValue(project.name); setEditingName(true); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                    style={{ color: 'var(--text-secondary)' }}
                    aria-label="Edit project name"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              )}
              <span
                className="px-3 py-1 rounded-full"
                style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)', background: 'rgba(124,58,237,0.10)', color: 'var(--primary)', borderRadius: 'var(--radius-full)' }}
              >
                {phaseConfig[project.currentPhase]?.label ?? project.currentPhase}
              </span>
              {BACKLOG_PHASES.has(project.currentPhase) && (
                <Link
                  to={`/projects/${id}/backlog`}
                  className="text-sm font-medium hover:underline"
                  style={{ color: 'var(--primary)', fontFamily: 'var(--font-body)' }}
                >
                  View Backlog →
                </Link>
              )}
              {/* Sprint 12.5 ACC.B3 (#417) — Delivery Mode badge.
                  Renders 'Governed' (--primary) by default for every
                  project today; 'Accelerate' (--signal) for projects
                  configured via the wizard's new Delivery Mode section.
                  Skip rendering for legacy projects whose API response
                  predates the deliveryMode column being included
                  (unlikely now that B1 has merged, but defensive). */}
              {/* MODE.Cfix.B1 — badge + plain explanation of the delivery
                  mode (renders for BOTH modes; fills the Accelerate gap left
                  by the Hand-Off-gated "What advancing means" popover).
                  `project.id === id` gate: on a project switch React renders
                  once with the new route id but the previous project still in
                  state (before the load effect flips the skeleton on); the
                  guard stops that frame showing the prior project's mode. */}
              {project.id === id && project.deliveryMode && <DeliveryModeExplainer mode={project.deliveryMode} />}
            </div>
            <div className="flex items-center gap-3">
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                Last activity: {formatRelativeTime(lastActivityTime)}
              </span>
              {/* phase-transition-overview — pre-action help. Mounted once
                  in the header row, gated on any Hand-Off button being
                  visible (one is shown at a time depending on currentPhase).
                  Sits before the active button so the help is reachable
                  before the click that triggers PhaseTransitionModal. */}
              {(showHandoffButton || showHandoff2to25 || showHandoff25to3 || showHandoff3to4) && (
                <HelpPopover
                  id="phase-transition-overview"
                  heading="What advancing means"
                  body={
                    <>
                      <p style={{ margin: '0 0 8px' }}>
                        Advancing closes the current phase and starts the next. The next-phase agent begins work immediately on the artefacts the new phase requires.
                      </p>
                      <p style={{ margin: '0 0 8px' }}>
                        Advancing runs a pre-flight check on this phase&apos;s gate criteria. The transition commits only when the check passes — if any criterion fails, the modal shows what&apos;s missing and you can fix it or override with a reason. In Governed Mode you trigger this by clicking Hand-Off; in Accelerate Mode the Phase Gate Agent triggers it as soon as the gate criteria are believed to be met.
                      </p>
                      <p style={{ margin: 0 }}>
                        Reversing a phase transition is possible but not trivial — the next-phase agent will have started producing artefacts, and rolling back means deciding what to do with that work. If you&apos;re not confident the current phase is complete, it&apos;s cheaper to delay advancing than to advance and revert.
                      </p>
                    </>
                  }
                />
              )}
              {showHandoffButton && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => void handleHandoff()}
                    disabled={!canHandoff}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(236,72,153,0.10)', color: 'var(--accent)', fontFamily: 'var(--font-body)' }}
                  >
                    <span>🚀</span>
                    {handingOff ? <><Spinner /> Handing Off…</> : 'Hand Off to Architecture →'}
                  </button>
                  {handoffError && <p className="text-xs" style={{ color: 'var(--error)' }}>{handoffError}</p>}
                </div>
              )}
              {showHandoff2to25 && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => void handleHandoff2to25()}
                    disabled={!canHandoff2to25}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(236,72,153,0.10)', color: 'var(--accent)', fontFamily: 'var(--font-body)' }}
                  >
                    <span>🚀</span>
                    {handingOff ? <><Spinner /> Handing Off…</> : 'Hand Off to UX Design →'}
                  </button>
                  {handoffError && <p className="text-xs" style={{ color: 'var(--error)' }}>{handoffError}</p>}
                </div>
              )}
              {showHandoff25to3 && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => void handleHandoff25to3()}
                    disabled={!canHandoff25to3}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(236,72,153,0.10)', color: 'var(--accent)', fontFamily: 'var(--font-body)' }}
                  >
                    <span>🚀</span>
                    {handingOff ? <><Spinner /> Handing Off…</> : 'Hand Off to Sprint Planning →'}
                  </button>
                  {handoffError && <p className="text-xs" style={{ color: 'var(--error)' }}>{handoffError}</p>}
                </div>
              )}
              {showHandoff3to4 && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => void handleHandoff3to4()}
                    disabled={!canHandoff3to4}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(236,72,153,0.10)', color: 'var(--accent)', fontFamily: 'var(--font-body)' }}
                  >
                    <span>🚀</span>
                    {handingOff ? <><Spinner /> Handing Off…</> : 'Hand Off to Development →'}
                  </button>
                  {handoffError && <p className="text-xs" style={{ color: 'var(--error)' }}>{handoffError}</p>}
                </div>
              )}
              {/* Sprint 11 UX.S1 — Standalone "Generate UX Specs" for projects past Phase 2.5. */}
              {showStandaloneUxButton && (
                <div className="flex flex-col items-end gap-1">
                  <button
                    data-testid="generate-ux-specs-btn"
                    onClick={handleGenerateUxClick}
                    disabled={startingStandaloneUx}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: '#EC4899', fontFamily: 'var(--font-body)' }}
                    title={hasUxArtefacts
                      ? 'UX specifications already exist — click to regenerate'
                      : 'Run the UX Design Agent without changing the project phase'}
                  >
                    <span>🎨</span>
                    {startingStandaloneUx
                      ? <><Spinner /> Starting…</>
                      : hasUxArtefacts ? 'Regenerate UX Specs →' : 'Generate UX Specs →'}
                  </button>
                  {standaloneUxError && <p className="text-xs" style={{ color: 'var(--error)' }}>{standaloneUxError}</p>}
                </div>
              )}
              {needsConfig && PHASE_AGENTS[project.currentPhase] ? (
                <div className="flex items-center gap-2">
                  {hasActiveSession ? (
                    <button
                      data-testid="continue-session-btn"
                      onClick={() => navigate(`/projects/${id}/chat`)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:shadow-[0_4px_14px_rgba(124,58,237,0.25)]"
                      style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600 }}
                    >
                      <span>💬</span>
                      Continue Session →
                    </button>
                  ) : FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT ? (
                    // No active session + in-chat config on: the agent asks
                    // config as opening turns, so the primary CTA is to open
                    // the chat. Without this, AGENT.A3 left users with NO
                    // actionable button in this state.
                    <StartSessionButton
                      agentDisplayName={PHASE_AGENTS[project.currentPhase] as string}
                      onStart={() => navigate(`/projects/${id}/chat`)}
                    />
                  ) : null}
                  {!FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT && (
                    <button
                      onClick={() => setShowConfigDialogue(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:shadow-[0_4px_14px_rgba(124,58,237,0.15)]"
                      style={{ background: 'rgba(124,58,237,0.10)', color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, border: '1px solid rgba(124,58,237,0.25)' }}
                    >
                      <span>⚙️</span>
                      Configure Phase
                    </button>
                  )}
                </div>
              ) : showDevButton ? (
                hasOpenIssues ? (
                  <div className="flex items-center gap-2" style={{ position: 'relative' }}>
                    <button
                      onClick={() => navigate(`/projects/${id}/chat?agentType=dev_agent`)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:shadow-[0_4px_14px_rgba(124,58,237,0.25)]"
                      style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600 }}
                    >
                      <span>⚡</span>
                      {hasSessions ? 'Resume Development' : 'Start Development →'}
                    </button>
                    {hasSessions && (
                      <>
                        <button
                          onClick={() => setShowNewSessionModal(true)}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg transition-all hover:shadow-sm"
                          style={{
                            background: 'transparent', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                            color: 'var(--primary)', border: '1px solid rgba(124,58,237,0.3)', cursor: 'pointer',
                          }}
                        >
                          + New Session
                        </button>
                        {devSessions.length > 1 && (
                          <div ref={sessionHistoryRef} style={{ position: 'relative' }}>
                            <button
                              onClick={() => setShowSessionHistory(prev => !prev)}
                              className="flex items-center px-2 py-2 rounded-lg transition-all hover:shadow-sm"
                              style={{
                                background: 'transparent', border: '1px solid var(--border)',
                                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12,
                              }}
                              title="Session history"
                            >
                              ▼
                            </button>
                            {showSessionHistory && (
                              <div
                                style={{
                                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                                  background: 'var(--surface)', border: '1px solid var(--border)',
                                  borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                  minWidth: 280, maxHeight: 300, overflowY: 'auto', zIndex: 50,
                                }}
                              >
                                <div style={{
                                  padding: '8px 12px', borderBottom: '1px solid var(--border)',
                                  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                                  color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
                                }}>
                                  Session History
                                </div>
                                {devSessions.map(s => (
                                  <button
                                    key={s.id}
                                    onClick={() => {
                                      setShowSessionHistory(false);
                                      navigate(`/projects/${id}/chat?sessionId=${s.id}`);
                                    }}
                                    style={{
                                      display: 'flex', flexDirection: 'column', gap: 2,
                                      width: '100%', padding: '10px 12px', border: 'none',
                                      background: 'none', cursor: 'pointer', textAlign: 'left',
                                      borderBottom: '1px solid var(--border)',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(124,58,237,0.04)')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{
                                        fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
                                      }}>
                                        {formatRelativeTime(s.updatedAt)}
                                      </span>
                                      <span style={{
                                        fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600,
                                        padding: '1px 6px', borderRadius: 'var(--radius-full)',
                                        background: s.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                                        color: s.status === 'active' ? '#10B981' : 'var(--text-secondary)',
                                      }}>
                                        {s.status}
                                      </span>
                                    </div>
                                    {s.selectedIssueTitle && (
                                      <span style={{
                                        fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                                        color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', maxWidth: 260,
                                      }}>
                                        {s.selectedIssueTitle}
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                      disabled
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-white opacity-40 cursor-not-allowed"
                      style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600 }}
                    >
                      <span>⚡</span>
                      Start Development →
                    </button>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {/* SL.D1 (#2951): issues come from the sprint, at Phase 4 entry or at its approval; there is no sync. */}
                      No open issues yet. Issues are created when a sprint is approved —{' '}
                      <span
                        onClick={() => navigate(`/projects/${id}/board`)}
                        style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        open the board
                      </span>
                    </p>
                  </div>
                )
              ) : (PHASE_AGENTS[project.currentPhase] && (
                hasSessions ? (
                  <button
                    onClick={() => navigate(`/projects/${id}/chat`)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:shadow-[0_4px_14px_rgba(124,58,237,0.25)]"
                    style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600 }}
                  >
                    <span>💬</span>
                    {project.currentPhase === 'phase_1_requirements' ? 'Resume Requirements'
                      : project.currentPhase === 'phase_2_architecture' ? 'Resume Architecture'
                      : project.currentPhase === 'phase_2_5_ux_design' ? 'Resume UX Design'
                      : project.currentPhase === 'phase_3_sprint_planning' ? 'Resume Sprint Planning'
                      : project.currentPhase === 'phase_4_development' ? 'Resume Development'
                      : project.currentPhase === 'phase_5_code_review' ? 'Resume Code Review'
                      : project.currentPhase === 'phase_6_testing' ? 'Resume QA'
                      : project.currentPhase === 'phase_7_deployment' ? 'Resume Deployment'
                      : 'Resume Conversation'}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/projects/${id}/chat`)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:shadow-[0_4px_14px_rgba(124,58,237,0.25)]"
                    style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600 }}
                  >
                    <span>🚀</span>
                    {project.currentPhase === 'phase_1_requirements' ? 'Start Requirements'
                      : project.currentPhase === 'phase_2_architecture' ? 'Start Architecture'
                      : project.currentPhase === 'phase_2_5_ux_design' ? 'Start UX Design'
                      : project.currentPhase === 'phase_3_sprint_planning' ? 'Start Sprint Planning'
                      : project.currentPhase === 'phase_4_development' ? 'Start Development'
                      : project.currentPhase === 'phase_5_code_review' ? 'Start Code Review'
                      : project.currentPhase === 'phase_6_testing' ? 'Start QA'
                      : project.currentPhase === 'phase_7_deployment' ? 'Start Deployment'
                      : 'Start Conversation'}
                  </button>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div
          className="px-8 border-b"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          {(['overview', 'team', 'stakeholders', 'meetings'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
              }}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
                padding: '10px 16px',
                cursor: 'pointer',
                marginRight: 4,
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Team tab — #2733: ProjectMembersPanel (replaces TeamPanel), one row
            per project_members row so PRIMARY/DELEGATE status is visible. */}
        {activeTab === 'team' && (
          <ProjectMembersPanel projectId={id!} />
        )}

        {/* Stakeholders tab — grants tenant-level CLIENT access (project_stakeholders +
            tenant_members on accept), distinct from Team's project-scoped roles above. */}
        {activeTab === 'stakeholders' && (
          <div className="px-8 py-6">
            <StakeholdersTab key={id} projectId={id!} projectTenantId={project?.tenantId ?? null} />
          </div>
        )}

        {/* Sprint 12 MPT.B2 — Meetings tab. Lists ingested meetings
            and lets the PM review extracted decisions / actions /
            risks per meeting. Backend lives at
            apps/api/src/routes/meetings.ts.

            `key={id}` re-mounts the component on project switch
            (Codex P2 on PR #737): without it, MeetingsTab keeps the
            previous project's `list` + `selectedMeetingId` until the
            new fetch lands, briefly showing rows / a detail view
            for a project the user has navigated away from. Idiomatic
            React reset — avoids syncing-state-in-effect, which the
            react-hooks/set-state-in-effect rule rejects. */}
        {activeTab === 'meetings' && (
          <MeetingsTab key={id} projectId={id!} />
        )}

        {/* Dashboard body */}
        {activeTab === 'overview' && (
        <div className="px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Left column (2/3) ─────────────────────── */}
            <div className="lg:col-span-2 space-y-4">

              {/* Gate status banner (entry) */}
              <div data-gate-banner>
                <GateStatusBanner
                  projectId={id ?? ''}
                  currentPhase={project.currentPhase}
                  canOverride
                  onOverrideSuccess={() => window.location.reload()}
                />
              </div>

              {/* DISC.B1 (#1476) — "Ready for Claude Design" banner. Fires when
                  all 5 Phase 2.5 artefacts are approved AND the effective Claude
                  Design export toggle (F4) is on, until the user dismisses it
                  (localStorage). Below the phase-gate status row for maximum
                  visibility on landing; renders independently of latestSession.
                  Client-side derivation from the artefacts already fetched. */}
              {id && (
                <ClaudeDesignReadyBanner
                  projectId={id}
                  artefacts={artefacts}
                  claudeDesignExportEnabled={project.claudeDesignExportEnabled}
                  tenantClaudeDesignExportEnabled={myTenant?.claudeDesignExportEnabled}
                />
              )}

              {/* #1115B S9 — GitHub repo setup banner. Renders only when the
                  project lacks a PROVISIONED GITHUB_REPO row (V4 symmetric
                  with the Phase 4 ENTRY gate-item). Mounted near the top of
                  the dashboard alongside GateStatusBanner so the user sees
                  it before navigating into Phase 4 transition affordances. */}
              <GitHubSetupBanner
                projectId={id ?? ''}
                binding={project.githubBinding ?? null}
              />


              {/* Phase config banner */}
              {configComplete && (
                <div style={{
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>
                    &#10003; Phase configured — workflow agent ready
                  </span>
                </div>
              )}
              {needsConfig && !showConfigDialogue && (
                <div style={{
                  background: 'rgba(124,58,237,0.06)',
                  border: '1px solid rgba(124,58,237,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT ? (
                      <>
                        Phase configuration is collected in the agent chat —{' '}
                        <Link
                          to={`/projects/${id}/chat`}
                          data-testid="banner-start-session-link"
                          style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline' }}
                        >
                          start a session to begin
                        </Link>
                        .
                      </>
                    ) : (
                      'Phase configuration required before the workflow agent becomes accessible.'
                    )}
                  </span>
                  {/* AGENT.A3: Configure button is only useful when the modal is available.
                      When PHASE_CONFIG_IN_CHAT is on, the button would silently no-op
                      (the modal render above is gated on the same flag), so we hide it. */}
                  {!FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT && (
                    <button
                      onClick={() => setShowConfigDialogue(true)}
                      style={{
                        fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, padding: '4px 12px',
                        border: '1px solid rgba(124,58,237,0.3)', borderRadius: 'var(--radius-sm)',
                        background: 'transparent', color: 'var(--primary)', cursor: 'pointer',
                      }}
                    >
                      Configure
                    </button>
                  )}
                </div>
              )}

              {/* Exit gate banner — passed with override */}
              {exitGateResult && exitGateResult.overallResult === 'PASS_WITH_OVERRIDE' && (showHandoffButton || showHandoff2to25 || showHandoff25to3 || showHandoff3to4) && (
                <div data-testid="exit-gate-overridden-banner" style={{
                  background: 'rgba(245,158,11,0.06)',
                  border: '1px solid rgba(245,158,11,0.25)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  marginBottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>&#9888;</span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)' }}>
                      Exit gate: <strong>passed with override</strong>
                    </span>
                  </div>
                  <button
                    onClick={() => setExitGatePanelOpen(true)}
                    style={{
                      background: 'none', border: '1px solid rgba(245,158,11,0.4)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--warning)',
                      fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                      padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    View Details
                  </button>
                </div>
              )}
              {/* Exit gate banner — shown when handoff available but exit gate fails */}
              {exitGateFailed && exitGateResult && (showHandoffButton || showHandoff2to25 || showHandoff25to3 || showHandoff3to4) && (() => {
                const failItems = exitGateResult.items.filter(i => i.status === 'FAIL');
                const exitPhaseNum = EXIT_GATE_PHASES[project.currentPhase];
                return (
                  <>
                    <div style={{
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.3)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '10px 14px',
                      marginBottom: 0,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: failItems.length > 0 ? 8 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>&#9888;</span>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)', fontWeight: 600 }}>
                            Exit gate: {failItems.length} item{failItems.length !== 1 ? 's' : ''} must pass before handoff
                          </span>
                        </div>
                        <button
                          onClick={() => setExitGatePanelOpen(true)}
                          style={{
                            background: 'none',
                            border: '1px solid rgba(245,158,11,0.4)',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--warning)',
                            fontFamily: 'var(--font-body)',
                            fontSize: 12, fontWeight: 600,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          View Details
                        </button>
                      </div>
                      {failItems.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 24 }}>
                          {failItems.slice(0, 2).map((item, i) => {
                            const text = item.actionRequired ?? item.title;
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                <span style={{ color: 'var(--warning)', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>&#10007;</span>
                                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {text.length > 100 ? text.substring(0, 100) + '...' : text}
                                </span>
                              </div>
                            );
                          })}
                          {failItems.length > 2 && (
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 19 }}>
                              and {failItems.length - 2} more...
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {exitPhaseNum && (
                      <GateReportPanel
                        projectId={id ?? ''}
                        phaseNumber={exitPhaseNum}
                        gateType="exit"
                        isOpen={exitGatePanelOpen}
                        onClose={() => setExitGatePanelOpen(false)}
                        canOverride
                        onOverrideSuccess={() => {
                          setExitGatePanelOpen(false);
                          window.location.reload();
                        }}
                      />
                    )}
                  </>
                );
              })()}

              {/* Current Phase card */}
              <div className={cardCls} style={cardStyle}>
                <SectionHeader color="var(--primary)">Current Phase</SectionHeader>
                <div className="flex items-start gap-4">
                  {/* Phase number badge — show the LOGICAL SDLC phase number
                      (`2.5` for UX Design, `3` for Sprint Planning) rather
                      than the array index of ALL_PHASES, which is shifted
                      by 1 for every phase past 2.5. */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center text-white font-bold"
                    style={{
                      width: 32, height: 32,
                      background: 'var(--primary)',
                      borderRadius: 'var(--radius-sm)',
                      fontFamily: 'var(--font-display)',
                      fontSize: 13,
                    }}
                  >
                    {PHASE_DISPLAY_NUMBER[project.currentPhase] ?? currentPhaseIndex}
                  </div>
                  <div>
                    <h3
                      style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}
                    >
                      {phaseConfig[project.currentPhase]?.label}
                    </h3>
                    <p className="mt-1" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
                      {phaseDescriptions[project.currentPhase]}
                    </p>
                    {PHASE_AGENTS[project.currentPhase] && (
                      <p className="mt-1" style={{ fontSize: 13, fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                        {/* Agent label is clickable when config-in-chat is ON — the whole
                            point of AGENT.A3 is that the agent collects config as opening
                            turns, so a direct chat link is safe. With the flag OFF we
                            preserve the legacy non-clickable appearance so the modal-first
                            flow isn't bypassed (Codex review on #632). */}
                        {FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT ? (
                          <>
                            <span style={{ color: 'var(--text-secondary)' }}>Agent: </span>
                            <Link
                              to={`/projects/${id}/chat`}
                              data-testid="agent-name-link"
                              style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                            >
                              {PHASE_AGENTS[project.currentPhase]}
                            </Link>
                          </>
                        ) : (
                          <span style={{ color: 'var(--primary)' }}>
                            Agent: {PHASE_AGENTS[project.currentPhase]}
                          </span>
                        )}
                      </p>
                    )}
                    <ContinueSessionCTA
                      activeSession={project.activeSession}
                      isRecent={activeSessionRecent}
                      onContinue={() => navigate(`/projects/${id}/chat`)}
                      formatRelativeTime={formatRelativeTime}
                    />
                    {project.currentPhaseState && (
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          Status: {project.currentPhaseState.status.replace(/_/g, ' ')}
                        </span>
                        {project.currentPhaseState.enteredAt && (
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            Started {formatRelativeTime(project.currentPhaseState.enteredAt)}
                          </span>
                        )}
                        {entryGateSummary?.hasFailures && (
                          <span
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 12, color: '#d97706', fontWeight: 600, cursor: 'pointer',
                            }}
                            onClick={() => {
                              const banner = document.querySelector('[data-gate-banner]');
                              if (banner) banner.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            | ⚠ {entryGateSummary.gateType === 'EXIT' ? 'Exit' : 'Entry'} gate: {entryGateSummary.failureCount} item{entryGateSummary.failureCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>



              {/* Phase Timeline — Sprint A.9 R3 fix: visual treatment for the
                  6-value status union now matches ProjectPhaseStepper's
                  STATUS_DOT mapping (awaiting_signoff = amber warning,
                  changes_requested = red error, blocked = red error).
                  Previously the 4-value switch dropped awaiting_signoff
                  and changes_requested into the grey/no-tooltip fallback
                  — a visible contradiction with the stepper on the same
                  page during client sign-off flows. */}
              {phaseTimeline.length > 0 && (
                <div className={cardCls} style={cardStyle}>
                  <SectionHeader color="var(--primary)">Phase Timeline</SectionHeader>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    {phaseTimeline.filter(p => p.phase !== 'phase_0_bootstrap').map((p, i, arr) => {
                      const dotColor: string = (() => {
                        switch (p.status) {
                          case 'completed':         return 'var(--success)';
                          case 'active':            return 'var(--primary)';
                          case 'awaiting_signoff':  return 'var(--warning, #d97706)';
                          case 'changes_requested': return 'var(--error, #dc2626)';
                          case 'blocked':           return 'var(--error, #dc2626)';
                          default:                  return 'var(--border)';
                        }
                      })();
                      const statusTooltip: string | undefined = (() => {
                        switch (p.status) {
                          case 'awaiting_signoff':  return 'Awaiting client sign-off';
                          case 'changes_requested': return 'Changes requested';
                          case 'blocked':           return 'Blocked';
                          default:                  return undefined;
                        }
                      })();
                      const textColor: string = (() => {
                        switch (p.status) {
                          case 'active':            return 'var(--primary)';
                          case 'completed':         return 'var(--text)';
                          case 'awaiting_signoff':  return 'var(--warning, #d97706)';
                          case 'changes_requested': return 'var(--error, #dc2626)';
                          case 'blocked':           return 'var(--error, #dc2626)';
                          default:                  return 'var(--text-secondary)';
                        }
                      })();
                      const fontWeight = p.status === 'active' ? 700 : 400;
                      return (
                        <span
                          key={p.phase}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title={statusTooltip}
                        >
                          <span style={{
                            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                            background: dotColor,
                            ...(p.status === 'active' ? { boxShadow: '0 0 0 3px rgba(124,58,237,0.2)' } : {}),
                          }} />
                          <span style={{
                            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight, color: textColor,
                          }}>
                            {p.displayName}
                          </span>
                          {p.status === 'completed' && (
                            <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 700 }}>&#10003;</span>
                          )}
                          {i < arr.length - 1 && (
                            <span style={{ color: 'var(--border)', fontSize: 11, margin: '0 2px' }}>&rarr;</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Phase 4 Development card */}
              {project.currentPhase === 'phase_4_development' && (
                <div className={cardCls} style={{ ...cardStyle, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.04)' }}>
                  <SectionHeader color="var(--success)">Development In Progress</SectionHeader>
                  <div className="flex items-start gap-3">
                    <span style={{ fontSize: 24 }}>🛠️</span>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>
                        Your project has been handed off to development.
                      </p>
                      <p className="mt-1" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                        All sprint planning artefacts are locked. The Phase 3 → Phase 4 handoff package is available in your artefacts.
                      </p>
                      {/* SL.D1 (#2951): no "Sync backlog to issues" button. A sprint's items get their issues through the sprint. */}
                      {/* SL.L3a (#2959, ruling D8): each sprint's actions and Plan next sprint */}
                      <Phase4SprintActions projectId={id!} onIssuesChanged={reloadOpenIssueCount} />
                      <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <Link
                          to={`/projects/${id}/board`}
                          style={{
                            display: 'inline-block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                            padding: '6px 14px', borderRadius: 'var(--radius-sm)', textDecoration: 'none',
                            background: 'var(--primary)', color: '#fff',
                          }}
                        >
                          View Board →
                        </Link>
                        <Link
                          to={`/artefacts?projectId=${id}`}
                          className="inline-block text-sm font-medium hover:underline"
                          style={{ color: 'var(--primary)', fontFamily: 'var(--font-body)' }}
                        >
                          View handoff package →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SL.L3a (#2959): the sprint settings card, directly below the Phase 4 card */}
              {project.currentPhase === 'phase_4_development' && (
                <SprintConfigCard projectId={id!} cardCls={cardCls} cardStyle={cardStyle} SectionHeader={SectionHeader} />
              )}

              {/* Phase 4 Calibration Report card */}
              {project.currentPhase === 'phase_4_development' && (
                <CalibrationCard projectId={id!} cardCls={cardCls} cardStyle={cardStyle} SectionHeader={SectionHeader} />
              )}

              {/* Phase 7 Deployment Dashboard */}
              {(project.currentPhase === 'phase_7_deployment' || project.currentPhase === 'phase_8_retrospective') && (
                <div className={cardCls} style={{ ...cardStyle, border: '1px solid rgba(20,184,166,0.3)', background: 'rgba(20,184,166,0.04)' }}>
                  <SectionHeader color="#14b8a6">Deployment Dashboard</SectionHeader>
                  <DeploymentDashboard projectId={id!} />
                </div>
              )}

              {/* Latest Session card */}
              {project.latestSession && (
                <div className={cardCls} style={cardStyle}>
                  <SectionHeader color="var(--success)">Latest Session</SectionHeader>
                  <div className="flex items-start gap-3">
                    {/* Agent avatar */}
                    <div className="flex-shrink-0">
                      <AgentAvatar agentType={project.latestSession.agentName} size={36} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                          {project.latestSession.agentDisplayName ?? project.latestSession.agentName}
                        </p>
                        {project.latestSession.status === 'active' && (
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{ fontSize: 11, fontWeight: 600, background: 'rgba(16,185,129,0.15)', color: 'var(--success)' }}
                          >
                            active
                          </span>
                        )}
                      </div>
                      {project.latestSession.summary && (
                        <p
                          className="line-clamp-3"
                          style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}
                        >
                          {project.latestSession.summary}
                        </p>
                      )}
                      <p className="mt-1" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {formatRelativeTime(project.latestSession.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity card */}
              <div className={cardCls} style={cardStyle}>
                <SectionHeader color="var(--accent)">Recent Activity</SectionHeader>
                {project.recentActivity.length === 0 ? (
                  <p className="text-center py-4" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                    No activity yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {project.recentActivity.slice(0, 5).map((item, i) => (
                      <div key={item.id}>
                        {i > 0 && <div className="mb-3" style={{ height: 1, background: 'var(--border)' }} />}
                        <div className="flex items-start gap-3">
                          <div
                            className="flex-shrink-0 flex items-center justify-center rounded-full"
                            style={{ width: 28, height: 28, background: 'rgba(124,58,237,0.12)', fontSize: 12 }}
                          >
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                              {item.text}
                            </p>
                            <p className="mt-0.5" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {formatRelativeTime(item.time)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right column (1/3) ─────────────────────── */}
            <div className="space-y-4">

              {/* Infrastructure card */}
              <InfrastructureCard projectId={id ?? ''} />

              {/* #3505 — shown when NewProjectModal's create-time pack
                  PATCH failed; sits directly above the card that fixes it. */}
              <DomainPackFailedNotice notice={packNotice} onDismiss={dismissPackNotice} />

              {/* Sprint 12 DOMAIN.A2 — Domain pack selection card.
                  PM can change the pack here; backend audits the
                  change and the next agent session picks up the
                  new pack. */}
              <ProjectDomainPackCard
                projectId={id ?? ''}
                currentType={project?.domainPackType ?? null}
                onChange={next => {
                  setProject(p => p ? { ...p, domainPackType: next } : p);
                  dismissPackNotice(); // #3505 — the pack is set; the notice is answered
                }}
              />

              {/* MODE.A2 (#1264) — delivery-mode switch card. Editable only
                  for TRIAL projects (fail-closed, inverted polarity vs the
                  creation-flow picker); read-only with a note otherwise.
                  Gated on project.deliveryMode being present (mirrors the
                  header badge at the top of this page). */}
              {project.deliveryMode && (
                <DeliveryModeSettingsCard
                  projectId={id ?? ''}
                  currentMode={project.deliveryMode}
                  billingState={myTenant?.billingState}
                  tenantLoading={tenantLoading}
                  currentPhase={project.currentPhase}
                  onChange={next => setProject(p => p ? { ...p, deliveryMode: next } : p)}
                />
              )}

              {/* AUTO.2 PR2 (#1404 follow-on) — QA test-execution language
                  card. `projects.language` is NOT NULL with a default, so
                  this is always present in practice; the guard mirrors the
                  defensive style already used for the other project-scoped
                  cards on this page. */}
              {project.language && (
                <QaLanguageSettingsCard
                  projectId={id ?? ''}
                  currentLanguage={project.language}
                  onChange={next => setProject(p => p ? { ...p, language: next } : p)}
                />
              )}

              {/* MODE.Cfix.B1 — mode transparency. Lists which phase EXIT
                  gates auto-advance vs pause for review (Accelerate), or the
                  compact all-pause state (Governed). Gated on deliveryMode,
                  mirroring the card above. */}
              {project.id === id && project.deliveryMode && (
                <AutonomousTransitionsPanel
                  // Key by identity so a mode toggle (via the card above)
                  // remounts the panel — its mount-time state is the reset.
                  // The `project.id === id` gate prevents a project switch from
                  // briefly mounting the panel with the new id but the previous
                  // project's mode, which would mis-fetch /projects/<new>/checkpoints.
                  key={`atp-${id ?? ''}-${project.deliveryMode}`}
                  projectId={id ?? ''}
                  deliveryMode={project.deliveryMode}
                />
              )}

              {/* Codebase intelligence card */}
              <CodebaseSidebarCard
                projectId={id ?? ''}
                latestSummaryArtefactId={
                  artefacts.find(a => a.type === 'codebase_summary')?.id ?? null
                }
              />

              {/* Escalation overrides card */}
              <EscalationOverridesSidebarCard projectId={id ?? ''} />

              {/* Project branding override card */}
              <ProjectBrandingOverrideSidebarCard projectId={id ?? ''} />

              {/* Project Claude Design export override card — UXV.F4 (#1420).
                  Tri-state Inherit/Enabled/Disabled; override + tenant default
                  come from data the page already holds (no new round-trip). */}
              <ProjectClaudeDesignOverrideSidebarCard
                projectId={id ?? ''}
                override={project.claudeDesignExportEnabled ?? null}
                tenantDefault={Boolean(myTenant?.claudeDesignExportEnabled)}
                onChange={next => setProject(p => p ? { ...p, claudeDesignExportEnabled: next } : p)}
              />

              {/* UXV.F1 (#1422) — Claude Design bundle import (Phase 2.5 ingest).
                  Project-scoped upload; NOT gated by the F4 export toggle.
                  Render ONLY when a concrete projectId is in scope — pass the
                  narrowed `id`, never an empty-string fallback, so the uploader
                  can't POST /projects//claude-design/upload (R1/R2 convergent). */}
              {id && (
                <ClaudeDesignBundleImportCard
                  projectId={id}
                  artefacts={artefacts}
                  // DISC.B2 (#1477) H1 — refetch artefacts after a successful
                  // ingest so the stage tracker advances to "Imported" without
                  // a page reload. Mirrors the initial artefact load above.
                  onIngested={() => {
                    apiFetch<ArtefactListItem[]>(`/artefacts?projectId=${id}`)
                      .then(data => setArtefacts(data))
                      .catch(() => {/* keep current list on refetch failure */});
                  }}
                />
              )}

              {/* QA policy & last session card (QA.A3 #950 AC5) */}
              <QAPolicyStatusCard projectId={id ?? ''} />

              {/* Project Info card */}
              <div className={cardCls} style={cardStyle}>
                <SectionHeader color="var(--text-secondary)">Project Info</SectionHeader>
                <dl style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  {[
                    { label: 'Product Area', value: project.productArea ?? 'Not specified' },
                    { label: 'Artefacts', value: String(project.artefactCount) },
                    { label: 'Created', value: formatRelativeTime(project.createdAt) },
                    { label: 'Last Updated', value: formatRelativeTime(project.updatedAt) },
                    ...(decisionsCount && decisionsCount.total > 0
                      ? [{ label: 'Decisions', value: `${decisionsCount.total} (${decisionsCount.accepted} accepted)` }]
                      : []),
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <dt
                        className="uppercase tracking-wider mb-0.5"
                        style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.05em' }}
                      >
                        {label}
                      </dt>
                      <dd style={{ fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                        {value}
                      </dd>
                    </div>
                  ))}
                  <div>
                    <dt
                      className="uppercase tracking-wider mb-0.5"
                      style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.05em' }}
                    >
                      Branch Protection
                    </dt>
                    <dd>
                      {protectionStatus ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                          style={
                            protectionStatus.protected
                              ? { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' }
                              : protectionStatus.canProtect
                              ? { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' }
                              : { background: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' }
                          }
                          title={protectionStatus.message}
                        >
                          {protectionStatus.protected ? 'Protected ✓' : protectionStatus.canProtect ? 'Protection available' : 'Protection unavailable'}
                        </span>
                      ) : (
                        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)' }}>—</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Amendments card */}
              <div className={cardCls} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                  <SectionHeader color="var(--warning)">Amendments</SectionHeader>
                  <Link
                    to={`/projects/${id}/amendments`}
                    style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    View all →
                  </Link>
                </div>
                {pendingAmendmentCount > 0 ? (
                  <Link
                    to={`/projects/${id}/amendments?status=proposed`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ fontSize: 13 }}>📝</span>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                      <strong>{pendingAmendmentCount}</strong> pending amendment{pendingAmendmentCount !== 1 ? 's' : ''}
                    </p>
                  </Link>
                ) : (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>No pending amendments</p>
                )}
                {staleArtefactCount > 0 && (
                  <Link
                    to={`/projects/${id}/amendments`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 10 }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ fontSize: 13 }}>⚠</span>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--warning)' }}>{staleArtefactCount}</strong> artefact{staleArtefactCount !== 1 ? 's' : ''} may need review
                    </p>
                  </Link>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Link
                    to={`/projects/${id}/amendments`}
                    style={{
                      display: 'inline-block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                      padding: '5px 12px', color: 'var(--primary)', background: 'transparent',
                      border: '1px solid rgba(124,58,237,0.3)', borderRadius: 'var(--radius-sm)', textDecoration: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; }}
                  >
                    + Create Amendment
                  </Link>
                  <button
                    onClick={() => setShowCreateCO(true)}
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                      padding: '5px 12px', color: 'var(--warning)', background: 'transparent',
                      border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.05)'; e.currentTarget.style.borderColor = 'var(--warning)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)'; }}
                  >
                    + Quick Change Order
                  </button>
                  <button
                    onClick={() => navigate(`/projects/${id}/chat?agentType=change_order`)}
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                      padding: '5px 12px', color: 'var(--warning)', background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.15)'; e.currentTarget.style.borderColor = 'var(--warning)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.08)'; e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)'; }}
                  >
                    + AI Change Order Agent
                  </button>
                  <Link
                    to={`/projects/${id}/change-orders`}
                    style={{
                      display: 'inline-block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                      padding: '5px 12px', color: 'var(--text-secondary)', background: 'transparent',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', textDecoration: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text-secondary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    View Change Orders →
                  </Link>
                </div>
              </div>

              {/* Refinement Suggestions card — Piece 4c (#2151) */}
              <div className={cardCls} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                  <SectionHeader color="var(--primary)">Refinement Suggestions</SectionHeader>
                  <Link
                    to={`/projects/${id}/refinement-suggestions`}
                    style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    View all →
                  </Link>
                </div>
                {pendingRefinementCount > 0 ? (
                  <Link
                    to={`/projects/${id}/refinement-suggestions?status=pending`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ fontSize: 13 }}>💡</span>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                      <strong>{pendingRefinementCount}</strong> pending suggestion{pendingRefinementCount !== 1 ? 's' : ''}
                    </p>
                  </Link>
                ) : unincorporatedResolvedCount > 0 ? (
                  // #2370 GAP 1 — "No pending suggestions" is only true once
                  // capture has actually run and found nothing to review.
                  // pendingRefinementCount alone can't tell a resolved-but-
                  // uncaptured answer apart from a genuinely clean queue —
                  // this branch is the truthful middle state: something IS
                  // waiting, it just hasn't been captured into a suggestion
                  // yet. Links to the capture-scan trigger (RefinementSuggestionsPage's
                  // "Generate suggestions" button, #2364) rather than
                  // auto-running the scan here — same manual, discoverable-
                  // not-automatic choice #2364 made for that button itself.
                  <Link
                    to={`/projects/${id}/refinement-suggestions`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ fontSize: 13 }}>⚠️</span>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)' }}>
                      <strong>{unincorporatedResolvedCount}</strong> resolved answer{unincorporatedResolvedCount !== 1 ? 's' : ''} not yet captured
                    </p>
                  </Link>
                ) : (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No pending suggestions</p>
                )}
              </div>

              {/* Decisions card */}
              {decisionsCount && decisionsCount.total > 0 && (
                <div className={cardCls} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                    <SectionHeader color="var(--primary)">Decisions</SectionHeader>
                    <Link
                      to={`/projects/${id}/decisions`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      View all →
                    </Link>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                    {decisionsCount.total}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    {decisionsCount.accepted} accepted · {decisionsCount.total - decisionsCount.accepted} pending
                  </p>
                </div>
              )}

              {/* Risks card */}
              {riskSummary && (
                <div className={cardCls} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                    <SectionHeader color="var(--warning, #f59e0b)">Risks</SectionHeader>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Link
                        to={`/projects/${id}/risks?add=1`}
                        style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        + Add Risk
                      </Link>
                      <Link
                        to={`/projects/${id}/risks`}
                        style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        View all →
                      </Link>
                    </div>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                    {riskSummary.total}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    {riskSummary.open} open
                    {riskSummary.critical > 0 && (
                      <> · <span style={{ color: '#ef4444', fontWeight: 600 }}>{riskSummary.critical} critical</span></>
                    )}
                    {riskSummary.high > 0 && riskSummary.critical === 0 && (
                      <> · <span style={{ color: '#f59e0b', fontWeight: 600 }}>{riskSummary.high} high</span></>
                    )}
                  </p>
                </div>
              )}

              {/* Traceability card */}
              {evidenceSummary && (
                <div className={cardCls} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                    <SectionHeader color="#7c3aed">Traceability</SectionHeader>
                    <Link
                      to={`/projects/${id}/traceability`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {evidenceSummary.totalLinks > 0 ? 'View matrix →' : 'Build evidence →'}
                    </Link>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                    {evidenceSummary.totalLinks}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    evidence links{evidenceSummary.totalLinks > 0 && (<> · {evidenceSummary.requirements} requirements · {evidenceSummary.userStories} stories</>)}
                  </p>
                </div>
              )}

              {/* Open Questions card — DEF-13. Previously this agenda
                  (/projects/:projectId/open-questions) was only reachable
                  via a conditional gate-failure link on this page; this
                  gives it a persistent entry alongside Decisions/Risks/
                  Traceability regardless of gate state. */}
              <div className={cardCls} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                  <SectionHeader color="var(--warning, #f59e0b)">Open Questions</SectionHeader>
                  <Link
                    to={`/projects/${id}/open-questions`}
                    style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    View all →
                  </Link>
                </div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                  Questions raised during elicitation and their resolutions.
                </p>
              </div>

              {/* NFRs card — #2612. Parity with the Risks/Open Questions
                  cards above: previously the §5 Non-Functional Requirements
                  disposition agenda (/projects/:projectId/nfrs) was only
                  reachable via a conditional Exit-Gate failure link
                  (GateReportPanel), which does not render once the gate has
                  passed. Gated on count > 0 (unlike the always-shown Open
                  Questions card above) — see undisposedNfrsCount's own
                  declaration for why. */}
              {undisposedNfrsCount !== null && undisposedNfrsCount > 0 && (
                <div className={cardCls} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                    <SectionHeader color="var(--warning, #f59e0b)">NFRs</SectionHeader>
                    <Link
                      to={`/projects/${id}/nfrs`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      View all →
                    </Link>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                    {undisposedNfrsCount}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    undisposed
                  </p>
                </div>
              )}

              {/* Knowledge Graph card — PKGV.B2 (#796). No unified
                  top-nav tab bar exists in this app (Q1 adjudication),
                  so the explorer's entry point is a card here, matching
                  the Decisions/Risks/Traceability precedent above. */}
              {FEATURE_FLAGS.PKG_EXPLORER_ENABLED && (
                <div className={cardCls} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                    <SectionHeader color="#0ea5e9">Knowledge Graph</SectionHeader>
                    <Link
                      to={`/projects/${id}/pkg-explorer`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      Explore →
                    </Link>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    Explore the full project graph with filters, layouts, and saved queries.
                  </p>
                </div>
              )}

              {/* Artefacts card */}
              <div className={cardCls} style={cardStyle}>
                <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-3)' }}>
                  <p
                    className="uppercase tracking-wider"
                    style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.05em', borderLeft: '3px solid var(--primary-light)', paddingLeft: 8 }}
                  >
                    Artefacts
                  </p>
                  {project.currentPhase === 'phase_1_requirements' && (
                    <div className="flex gap-2">
                      <Link
                        to={`/projects/${id}/import`}
                        className="px-2 py-1 rounded transition-colors hover:opacity-80"
                        style={{ fontSize: 12, fontFamily: 'var(--font-body)', background: 'var(--surface-alt)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                      >
                        Import
                      </Link>
                      <button
                        onClick={handleRunAnalysis}
                        disabled={analysing || artefacts.length === 0}
                        className="px-2 py-1 rounded transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontSize: 12, fontFamily: 'var(--font-body)', background: 'var(--primary)', color: 'white' }}
                      >
                        {analysing ? 'Analysing…' : 'Run Analysis'}
                      </button>
                    </div>
                  )}
                </div>
                {artefactsLoading ? (
                  <div className="space-y-2 animate-pulse">
                    <div className="h-9 rounded-lg" style={{ background: 'var(--surface-alt)' }} />
                    <div className="h-9 rounded-lg" style={{ background: 'var(--surface-alt)' }} />
                  </div>
                ) : artefacts.length === 0 ? (
                  <p className="text-center py-4" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                    No artefacts yet
                  </p>
                ) : (
                  <div>
                    {groupedArtefacts.map(({ phase, items }) => {
                      const isExpanded = expandedArtefactPhases.has(phase);
                      const typeCounts = items.reduce<Record<string, number>>((acc, a) => {
                        acc[a.type] = (acc[a.type] ?? 0) + 1;
                        return acc;
                      }, {});
                      return (
                        <div key={phase}>
                          <div
                            onClick={() => toggleArtefactPhase(phase)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 4px 4px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}>▶</span>
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{phase}</span>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)', opacity: 0.6 }}>({items.length})</span>
                          </div>
                          {isExpanded && items.map(a => {
                            const mapping = ARTEFACT_PHASE_MAP[a.type];
                            const icon = mapping?.icon ?? '📄';
                            const isDuplicate = (typeCounts[a.type] ?? 0) > 1;
                            const displayName = isDuplicate
                              ? `${mapping?.shortName ?? a.title} (v${a.currentVersion})`
                              : (mapping?.shortName ?? a.title);
                            return (
                              <Link
                                key={a.id}
                                to={`/artefacts?tabs=${a.id}&active=${a.id}`}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px 6px 8px', borderRadius: 'var(--radius-sm)', textDecoration: 'none' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              >
                                <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>v{a.currentVersion}</span>
                                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 500, padding: '2px 6px', borderRadius: 9999, ...statusPillStyle(a.status) }}>
                                  {a.status === 'handed_off' ? 'Locked' : a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Phase Progress card — A.9 anchor for ?phase=<key> scrollIntoView.
                  scroll-margin-top compensates for the sticky ProjectPhaseStepper
                  (~50px nav + the dashboard's own sticky breadcrumb above this
                  point) so the card lands BELOW the sticky chrome, not under
                  it. Without this, scrollIntoView({block:'start'}) puts the
                  card's top at viewport top, hidden behind sticky elements,
                  presenting as "main area empty/black after click". */}
              <div id="phase-progress-card" className={cardCls} style={{ ...cardStyle, scrollMarginTop: '120px' }}>
                <SectionHeader color="var(--success)">Phase Progress</SectionHeader>
                <div>
                  {ALL_PHASES.map((phase, index) => {
                    const isCompleted = index < currentPhaseIndex;
                    const isCurrent = index === currentPhaseIndex;
                    const label = phaseConfig[phase]?.label ?? phase;
                    const isLast = index === ALL_PHASES.length - 1;
                    const agentName = PHASE_AGENTS[phase];
                    const isExpanded = expandedPhase === phase;

                    const phaseSession = isCompleted ? sessions.find(s => s.phase === phase) ?? null : null;
                    const phaseArtefacts = isCompleted ? artefacts.filter(a => (PHASE_ARTEFACT_TYPES[phase] ?? []).includes(a.type)) : [];
                    const phaseState = isCompleted ? allPhaseStates.find(ps => ps.phase === phase) ?? null : null;

                    // Circle style
                    const circleStyle: React.CSSProperties = isCompleted
                      ? { background: 'var(--success)', color: 'white' }
                      : isCurrent
                      ? { background: 'var(--primary)', color: 'white', boxShadow: '0 0 0 4px rgba(124,58,237,0.15)' }
                      : { background: 'var(--border)', color: 'var(--text-secondary)' };

                    const lineStyle: React.CSSProperties = {
                      width: 2,
                      background: isCompleted ? 'var(--success)' : 'var(--border)',
                    };

                    return (
                      <div key={phase}>
                        <div className="flex items-stretch gap-3">
                          {/* Stepper column */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div
                              className="flex items-center justify-center flex-shrink-0 font-bold"
                              style={{ width: 24, height: 24, borderRadius: '50%', fontSize: 11, ...circleStyle }}
                            >
                              {isCompleted ? '✓' : (PHASE_DISPLAY_NUMBER[phase] ?? index)}
                            </div>
                            {!isLast && (
                              <div className="flex-1 min-h-[20px]" style={lineStyle} />
                            )}
                          </div>

                          {/* Label */}
                          {isCompleted ? (
                            <button
                              onClick={() => setExpandedPhase(prev => prev === phase ? null : phase)}
                              className="flex-1 text-left flex items-start gap-1 pb-3 group"
                            >
                              <div className="flex-1">
                                <span
                                  className="group-hover:underline"
                                  style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, color: 'var(--text)' }}
                                >
                                  {label}
                                </span>
                                {agentName && (
                                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                                    {agentName}
                                  </p>
                                )}
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                {isExpanded ? '▲' : '▼'}
                              </span>
                            </button>
                          ) : (
                            <div className="flex-1 pb-3">
                              <span
                                style={{
                                  fontFamily: 'var(--font-body)',
                                  fontSize: 14,
                                  fontWeight: isCurrent ? 600 : 500,
                                  color: isCurrent ? 'var(--primary)' : 'var(--text-secondary)',
                                }}
                              >
                                {label}
                              </span>
                              {agentName && isCurrent && (
                                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                                  {agentName}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Phase Detail Card (expanded) */}
                        {isCompleted && isExpanded && (
                          <div
                            className="mb-2 space-y-2"
                            style={{
                              marginLeft: 34,
                              background: 'var(--surface-alt)',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              padding: 'var(--space-3)',
                            }}
                          >
                            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>
                              Agent: {PHASE_AGENTS[phase] ?? 'Unknown'}
                            </p>
                            {phaseState?.exitedAt && (
                              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                Completed {formatRelativeTime(phaseState.exitedAt)}
                              </p>
                            )}
                            {phaseArtefacts.length > 0 && (
                              <div>
                                <p className="mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
                                  Artefacts:
                                </p>
                                {phaseArtefacts.map(a => (
                                  <Link
                                    key={a.id}
                                    to={`/artefacts?tabs=${a.id}&active=${a.id}`}
                                    className="flex items-center gap-1.5 py-0.5 hover:underline"
                                    style={{ fontSize: 12, color: 'var(--primary)' }}
                                  >
                                    <span>{ARTEFACT_ICONS[a.type] ?? '📄'}</span>
                                    <span>{a.title}</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>— Locked</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                            {phaseSession?.summary && (
                              <p className="line-clamp-2 italic" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {phaseSession.summary}
                              </p>
                            )}
                            {phaseSession && (
                              <Link
                                to={`/projects/${id}/chat?sessionId=${phaseSession.id}`}
                                className="hover:underline"
                                style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}
                              >
                                View Chat History →
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div
              style={{
                marginTop: 'var(--space-8)',
                border: '1px solid var(--error)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-6)',
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--error)',
                  marginBottom: 'var(--space-4)',
                }}
              >
                Danger Zone
              </h3>

              {/* Archive */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-4) 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    Archive this project
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Mark as read-only. You can restore it later.
                  </p>
                </div>
                <button
                  onClick={handleArchive}
                  disabled={archiving}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--error)',
                    background: 'transparent',
                    color: 'var(--error)',
                    cursor: archiving ? 'not-allowed' : 'pointer',
                    opacity: archiving ? 0.6 : 1,
                  }}
                >
                  {archiving ? 'Archiving...' : 'Archive Project'}
                </button>
              </div>

              {/* Delete */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-4)',
                }}
              >
                <div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    Delete this project
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Permanently removes the project after 30 days. This action can be undone within the grace period.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'var(--error)',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

      {/* Phase Configuration Dialogue modal — gated on PHASE_CONFIG_IN_CHAT.
          When the flag is true (default), config is collected in the agent
          chat instead (AGENT.A1/A2/A3). The modal component is kept in the
          bundle so flipping the flag off is a zero-code rollback. */}
      {!FEATURE_FLAGS.PHASE_CONFIG_IN_CHAT && showConfigDialogue && project && (
        <PhaseConfigDialogue
          projectId={id!}
          phase={project.currentPhase}
          phaseName={phaseConfig[project.currentPhase]?.label ?? project.currentPhase}
          onComplete={() => {
            setShowConfigDialogue(false);
            setConfigComplete(true);
          }}
          onClose={() => setShowConfigDialogue(false)}
        />
      )}

      {transitionModal && (
        <PhaseTransitionModal
          projectId={id!}
          fromPhase={transitionModal.fromPhase}
          toPhaseNumber={transitionModal.toPhaseNumber}
          direction={transitionModal.direction}
          isOpen={true}
          onClose={() => setTransitionModal(null)}
          onSuccess={handleTransitionSuccess}
        />
      )}

      {exitGatePanelOpen && project && EXIT_GATE_PHASES[project.currentPhase] && (
        <GateReportPanel
          projectId={id!}
          phaseNumber={EXIT_GATE_PHASES[project.currentPhase]}
          gateType="exit"
          isOpen={exitGatePanelOpen}
          onClose={() => setExitGatePanelOpen(false)}
          canOverride
          onOverrideSuccess={() => {
            setExitGatePanelOpen(false);
            // Re-fetch exit gate status
            const phaseNum = EXIT_GATE_PHASES[project.currentPhase];
            if (phaseNum) {
              apiFetch<{ overallResult: 'PASS' | 'FAIL' | 'PASS_WITH_OVERRIDE'; items: { title: string; status: string; actionRequired?: string }[] }>(
                `/projects/${id}/phases/${phaseNum}/gate/exit`,
              )
                .then(setExitGateResult)
                .catch(() => setExitGateResult(null));
            }
          }}
        />
      )}

      {/* New Session confirmation modal */}
      {showNewSessionModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setShowNewSessionModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)', padding: 24, maxWidth: 420, width: '90%',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Start a new Dev Agent session?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Your current session will be saved. You can resume it from the session history at any time.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowNewSessionModal(false)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleConfirmNewSession()}
                disabled={creatingNewSession}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: creatingNewSession ? 'not-allowed' : 'pointer',
                  opacity: creatingNewSession ? 0.6 : 1,
                }}
              >
                {creatingNewSession ? 'Creating...' : 'Start New Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && project && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => { setShowDeleteConfirm(false); setConfirmName(''); }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)', padding: 24, maxWidth: 440, width: '90%',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
              Delete project?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 16 }}>
              This will soft-delete <strong>{project.name}</strong>. It can be restored within 30 days, after which it is permanently removed.
            </p>
            <label style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Type <strong>{project.name}</strong> to confirm:
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={e => setConfirmName(e.target.value)}
              placeholder={project.name}
              style={{
                width: '100%', padding: '8px 12px', fontSize: 14,
                fontFamily: 'var(--font-body)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                marginBottom: 20,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowDeleteConfirm(false); setConfirmName(''); }}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDelete()}
                disabled={confirmName !== project.name || deleting}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--error)', color: '#fff',
                  cursor: confirmName !== project.name || deleting ? 'not-allowed' : 'pointer',
                  opacity: confirmName !== project.name || deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateCO && id && (
        <CreateChangeOrderModal
          projectId={id}
          onClose={() => setShowCreateCO(false)}
          onCreated={() => setShowCreateCO(false)}
        />
      )}

      {/* Sprint 11 UX.S1 — "Already exists" guard for standalone UX runs. */}
      {showStandaloneUxConfirm && (
        <div
          data-testid="standalone-ux-confirm"
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setShowStandaloneUxConfirm(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)', padding: 24, maxWidth: 460, width: '90%',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              UX specifications already exist
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              This project already has a User Journey Map. Would you like to regenerate? A new version will be created — the existing artefact stays in place.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowStandaloneUxConfirm(false)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowStandaloneUxConfirm(false);
                  navigate('/artefacts');
                }}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                View existing
              </button>
              <button
                data-testid="standalone-ux-regenerate"
                onClick={() => {
                  setShowStandaloneUxConfirm(false);
                  void startStandaloneUxSession();
                }}
                disabled={startingStandaloneUx}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: '#EC4899', color: '#fff',
                  cursor: startingStandaloneUx ? 'not-allowed' : 'pointer',
                  opacity: startingStandaloneUx ? 0.6 : 1,
                }}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
